// UltraFlex Member Hub - Enhanced Profile & Settings
import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Link } from 'react-router-dom';
import { 
  User, Mail, Phone, MapPin, Calendar, Shield, CreditCard,
  Edit, Check, X, ChevronLeft, AlertCircle, Clock, Bell, LogOut,
  Lock, Snowflake, Home, UserPlus, Camera
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MemberProfile = () => {
  const { user, token, logout } = useAuth();
  const [memberData, setMemberData] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [freezeHistory, setFreezeHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [activeSection, setActiveSection] = useState(null); // 'profile', 'address', 'password', 'freeze'
  const [formData, setFormData] = useState({});
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  // Password change state
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });

  // Freeze request state
  const [freezeData, setFreezeData] = useState({
    start_date: '',
    end_date: '',
    reason: ''
  });

  // Direct Debit state
  const [mandate, setMandate] = useState(null);
  const [ddForm, setDdForm] = useState({ account_holder_name: '', account_number: '', sort_code: '' });
  const [ddSaving, setDdSaving] = useState(false);

  useEffect(() => {
    fetchMemberData();
  }, []);

  const fetchMemberData = async () => {
    setLoading(true);
    try {
      // Fetch member profile
      const memberRes = await fetch(`${API_URL}/api/members/${user.id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const memberDetails = await memberRes.json();
      setMemberData(memberDetails);
      setFormData({
        phone: memberDetails.phone || '',
        address_line1: memberDetails.address_line1 || '',
        address_line2: memberDetails.address_line2 || '',
        city: memberDetails.city || '',
        postcode: memberDetails.postcode || '',
        emergency_contact_name: memberDetails.emergency_contact_name || '',
        emergency_contact_phone: memberDetails.emergency_contact_phone || '',
        marketing_opt_in: memberDetails.marketing_opt_in || false
      });

      // Fetch subscription
      const subRes = await fetch(`${API_URL}/api/subscriptions?member_id=${user.id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const subData = await subRes.json();
      if (subData.items && subData.items.length > 0) {
        setSubscription(subData.items[0]);
      }

      // Fetch payment history
      const paymentsRes = await fetch(`${API_URL}/api/collections?member_id=${user.id}&page_size=10`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const paymentsData = await paymentsRes.json();
      setPaymentHistory(paymentsData.items || []);

      // Fetch freeze history
      try {
        const freezeRes = await fetch(`${API_URL}/api/membership-freezes?member_id=${user.id}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const freezeData = await freezeRes.json();
        setFreezeHistory(freezeData.items || []);
      } catch (e) {
        console.log('Freeze history not available');
      }

      // Fetch mandate
      try {
        const mandateRes = await fetch(`${API_URL}/api/auth/my-mandate`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (mandateRes.ok) {
          const mandateData = await mandateRes.json();
          setMandate(mandateData.mandate);
        }
      } catch (e) {
        console.log('Mandate not available');
      }
    } catch (error) {
      console.error('Failed to fetch member data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveProfile = async () => {
    setSaving(true);
    setMessage(null);
    try {
      const res = await fetch(`${API_URL}/api/members/${user.id}`, {
        method: 'PATCH',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });
      if (!res.ok) throw new Error('Failed to save');
      await fetchMemberData();
      setActiveSection(null);
      setMessage({ type: 'success', text: 'Profile updated successfully!' });
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to save profile' });
    } finally {
      setSaving(false);
    }
  };

  const [pwdError, setPwdError] = useState('');

  const handleChangePassword = async () => {
    setPwdError('');
    if (!passwordData.current_password) {
      setPwdError('Current password is required');
      return;
    }
    if (!passwordData.new_password) {
      setPwdError('New password is required');
      return;
    }
    if (passwordData.new_password.length < 8) {
      setPwdError('New password must be at least 8 characters');
      return;
    }
    if (passwordData.new_password !== passwordData.confirm_password) {
      setPwdError('New passwords do not match');
      return;
    }

    setSaving(true);
    setMessage(null);
    try {
      const res = await fetch(`${API_URL}/api/auth/change-password`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          current_password: passwordData.current_password,
          new_password: passwordData.new_password
        })
      });
      
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Failed to change password');
      }
      
      setPasswordData({ current_password: '', new_password: '', confirm_password: '' });
      setActiveSection(null);
      setMessage({ type: 'success', text: 'Password changed successfully!' });
    } catch (error) {
      setPwdError(error.message || 'Current password is incorrect');
      setMessage({ type: 'error', text: error.message });
    } finally {
      setSaving(false);
    }
  };

  const [freezeError, setFreezeError] = useState('');

  const handleRequestFreeze = async () => {
    setFreezeError('');
    if (!freezeData.start_date || !freezeData.end_date) {
      setFreezeError('Please select both start and end dates.');
      return;
    }

    const startDate = new Date(freezeData.start_date);
    const endDate = new Date(freezeData.end_date);
    const today = new Date();
    today.setHours(0,0,0,0);
    const daysDiff = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24));
    const daysFromNow = Math.ceil((startDate - today) / (1000 * 60 * 60 * 24));

    if (daysFromNow < 7) {
      setFreezeError('Start date must be at least 7 days from today.');
      return;
    }
    if (daysDiff < 7) {
      setFreezeError('Minimum freeze period is 7 days.');
      return;
    }
    if (daysDiff > 90) {
      setFreezeError('Maximum freeze period is 90 days.');
      return;
    }
    if (endDate <= startDate) {
      setFreezeError('End date must be after start date.');
      return;
    }

    setSaving(true);
    setMessage(null);
    try {
      const res = await fetch(`${API_URL}/api/membership-freezes`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          member_id: user.id,
          start_date: freezeData.start_date,
          end_date: freezeData.end_date,
          reason: freezeData.reason || 'Member request'
        })
      });
      
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Failed to request freeze');
      }
      
      setFreezeData({ start_date: '', end_date: '', reason: '' });
      setActiveSection(null);
      setMessage({ type: 'success', text: 'Membership freeze requested! We will contact you to confirm.' });
      fetchMemberData();
    } catch (error) {
      setMessage({ type: 'error', text: error.message });
    } finally {
      setSaving(false);
    }
  };

  const getStatusBadge = (status) => {
    const config = {
      active: { bg: 'bg-green-500/20', text: 'text-green-400', label: 'Active' },
      paid: { bg: 'bg-green-500/20', text: 'text-green-400', label: 'Paid' },
      grace: { bg: 'bg-orange-500/20', text: 'text-orange-400', label: 'Grace Period' },
      suspended: { bg: 'bg-red-500/20', text: 'text-red-400', label: 'Suspended' },
      pending: { bg: 'bg-yellow-500/20', text: 'text-yellow-400', label: 'Pending' },
      frozen: { bg: 'bg-blue-500/20', text: 'text-blue-400', label: 'Frozen' },
      cancelled: { bg: 'bg-gray-500/20', text: 'text-gray-400', label: 'Cancelled' }
    };
    const c = config[status] || { bg: 'bg-gray-500/20', text: 'text-gray-400', label: status };
    return (
      <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-sm font-medium ${c.bg} ${c.text}`}>
        {c.label}
      </span>
    );
  };

  const getMinFreezeDate = () => {
    const date = new Date();
    date.setDate(date.getDate() + 7); // Minimum 7 days from now
    return date.toISOString().split('T')[0];
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-uf-bg flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-uf-red"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-uf-bg pb-20" data-testid="member-profile-page">
      {/* Header */}
      <div className="bg-uf-surface border-b border-uf-border px-4 py-4 sticky top-0 z-10">
        <div className="max-w-lg mx-auto flex items-center justify-between">
          <Link to="/hub" className="flex items-center text-uf-text-muted hover:text-uf-text">
            <ChevronLeft className="w-5 h-5 mr-1" />
            Back
          </Link>
          <h1 className="text-lg font-bold text-uf-text">Profile & Settings</h1>
          <div className="w-16"></div>
        </div>
      </div>

      <div className="px-4 py-6">
        <div className="max-w-lg mx-auto space-y-6">
          {/* Message Toast */}
          {message && (
            <div className={`p-4 rounded-lg flex items-center ${message.type === 'success' ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>
              {message.type === 'success' ? <Check className="w-5 h-5 mr-2" /> : <AlertCircle className="w-5 h-5 mr-2" />}
              {message.text}
              <button onClick={() => setMessage(null)} className="ml-auto">
                <X className="w-4 h-4" />
              </button>
            </div>
          )}

          {/* Profile Card */}
          <div className="bg-uf-surface rounded-xl border border-uf-border overflow-hidden">
            <div className="bg-gradient-to-r from-uf-red/20 to-transparent p-6">
              <div className="flex items-center">
                <div className="relative">
                  <div className="w-20 h-20 rounded-full bg-uf-bg flex items-center justify-center border-2 border-uf-red">
                    <span className="text-uf-red text-2xl font-bold">
                      {user?.first_name?.[0]}{user?.last_name?.[0]}
                    </span>
                  </div>
                  <button className="absolute -bottom-1 -right-1 w-8 h-8 bg-uf-red rounded-full flex items-center justify-center text-white hover:bg-uf-red-hover">
                    <Camera className="w-4 h-4" />
                  </button>
                </div>
                <div className="ml-4">
                  <h2 className="text-xl font-bold text-uf-text">
                    {user?.first_name} {user?.last_name}
                  </h2>
                  <p className="text-uf-text-muted text-sm font-mono">{memberData?.member_number}</p>
                  {getStatusBadge(memberData?.subscription_status)}
                </div>
              </div>
            </div>

            <div className="p-6 space-y-4">
              <div className="flex items-center text-uf-text-muted">
                <Mail className="w-5 h-5 mr-3 text-uf-red flex-shrink-0" />
                <span className="truncate">{user?.email}</span>
                {memberData?.email_verified && (
                  <Check className="w-4 h-4 ml-2 text-green-400" title="Verified" />
                )}
              </div>
              
              <div className="flex items-center text-uf-text-muted">
                <Phone className="w-5 h-5 mr-3 text-uf-red flex-shrink-0" />
                <span>{memberData?.phone || 'Not set'}</span>
              </div>

              <div className="flex items-center text-uf-text-muted">
                <MapPin className="w-5 h-5 mr-3 text-uf-red flex-shrink-0" />
                <span>{memberData?.address_line1}, {memberData?.city}, {memberData?.postcode}</span>
              </div>

              <div className="flex items-center text-uf-text-muted">
                <Calendar className="w-5 h-5 mr-3 text-uf-red flex-shrink-0" />
                <span>Member since {new Date(memberData?.created_at).toLocaleDateString()}</span>
              </div>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="grid grid-cols-2 gap-3">
            <button
              onClick={() => setActiveSection(activeSection === 'profile' ? null : 'profile')}
              className={`p-4 rounded-xl border text-left transition-all ${activeSection === 'profile' ? 'bg-uf-red/20 border-uf-red' : 'bg-uf-surface border-uf-border hover:border-uf-red/50'}`}
              data-testid="edit-profile-btn"
            >
              <Edit className="w-5 h-5 text-uf-red mb-2" />
              <p className="text-uf-text font-medium">Edit Profile</p>
              <p className="text-uf-text-muted text-xs">Phone, emergency contact</p>
            </button>

            <button
              onClick={() => setActiveSection(activeSection === 'address' ? null : 'address')}
              className={`p-4 rounded-xl border text-left transition-all ${activeSection === 'address' ? 'bg-uf-red/20 border-uf-red' : 'bg-uf-surface border-uf-border hover:border-uf-red/50'}`}
              data-testid="edit-address-btn"
            >
              <Home className="w-5 h-5 text-uf-red mb-2" />
              <p className="text-uf-text font-medium">Address</p>
              <p className="text-uf-text-muted text-xs">Update your address</p>
            </button>

            <button
              onClick={() => setActiveSection(activeSection === 'password' ? null : 'password')}
              className={`p-4 rounded-xl border text-left transition-all ${activeSection === 'password' ? 'bg-uf-red/20 border-uf-red' : 'bg-uf-surface border-uf-border hover:border-uf-red/50'}`}
              data-testid="change-password-btn"
            >
              <Lock className="w-5 h-5 text-uf-red mb-2" />
              <p className="text-uf-text font-medium">Password</p>
              <p className="text-uf-text-muted text-xs">Change your password</p>
            </button>

            <button
              onClick={() => setActiveSection(activeSection === 'freeze' ? null : 'freeze')}
              className={`p-4 rounded-xl border text-left transition-all ${activeSection === 'freeze' ? 'bg-blue-500/20 border-blue-500' : 'bg-uf-surface border-uf-border hover:border-blue-500/50'}`}
              data-testid="freeze-membership-btn"
            >
              <Snowflake className="w-5 h-5 text-blue-400 mb-2" />
              <p className="text-uf-text font-medium">Freeze</p>
              <p className="text-uf-text-muted text-xs">Pause membership</p>
            </button>

            <button
              onClick={() => setActiveSection(activeSection === 'banking' ? null : 'banking')}
              className={`p-4 rounded-xl border text-left transition-all ${activeSection === 'banking' ? 'bg-emerald-500/20 border-emerald-500' : 'bg-uf-surface border-uf-border hover:border-emerald-500/50'}`}
              data-testid="banking-details-btn"
            >
              <CreditCard className="w-5 h-5 text-emerald-400 mb-2" />
              <p className="text-uf-text font-medium">Banking</p>
              <p className="text-uf-text-muted text-xs">Update DD details</p>
            </button>
          </div>

          {/* Edit Profile Section */}
          {activeSection === 'profile' && (
            <div className="bg-uf-surface rounded-xl border border-uf-border p-6" data-testid="edit-profile-section">
              <h3 className="text-lg font-semibold text-uf-text mb-4">Edit Profile</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm text-uf-text-muted mb-1">Phone Number</label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    className="w-full px-4 py-3 bg-gray-100 border border-gray-300 rounded-lg text-black focus:ring-2 focus:ring-uf-red focus:outline-none"
                    placeholder="07123 456789"
                  />
                </div>
                <div>
                  <label className="block text-sm text-uf-text-muted mb-1">Emergency Contact Name</label>
                  <input
                    type="text"
                    value={formData.emergency_contact_name}
                    onChange={(e) => setFormData({...formData, emergency_contact_name: e.target.value})}
                    className="w-full px-4 py-3 bg-gray-100 border border-gray-300 rounded-lg text-black focus:ring-2 focus:ring-uf-red focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm text-uf-text-muted mb-1">Emergency Contact Phone</label>
                  <input
                    type="tel"
                    value={formData.emergency_contact_phone}
                    onChange={(e) => setFormData({...formData, emergency_contact_phone: e.target.value})}
                    className="w-full px-4 py-3 bg-gray-100 border border-gray-300 rounded-lg text-black focus:ring-2 focus:ring-uf-red focus:outline-none"
                  />
                </div>
                <label className="flex items-center gap-3 py-2">
                  <input
                    type="checkbox"
                    checked={formData.marketing_opt_in}
                    onChange={(e) => setFormData({...formData, marketing_opt_in: e.target.checked})}
                    className="w-5 h-5 rounded border-uf-border text-uf-red focus:ring-uf-red"
                  />
                  <span className="text-uf-text">Receive marketing communications</span>
                </label>
                <div className="flex gap-3 pt-4">
                  <button
                    onClick={() => setActiveSection(null)}
                    className="flex-1 py-3 border border-uf-border text-uf-text rounded-lg hover:bg-uf-surface"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSaveProfile}
                    disabled={saving}
                    className="flex-1 py-3 bg-uf-red text-white rounded-lg hover:bg-uf-red-hover disabled:opacity-50"
                  >
                    {saving ? 'Saving...' : 'Save Changes'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Edit Address Section */}
          {activeSection === 'address' && (
            <div className="bg-uf-surface rounded-xl border border-uf-border p-6" data-testid="edit-address-section">
              <h3 className="text-lg font-semibold text-uf-text mb-4">Update Address</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm text-uf-text-muted mb-1">Address Line 1</label>
                  <input
                    type="text"
                    value={formData.address_line1}
                    onChange={(e) => setFormData({...formData, address_line1: e.target.value})}
                    className="w-full px-4 py-3 bg-gray-100 border border-gray-300 rounded-lg text-black focus:ring-2 focus:ring-uf-red focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm text-uf-text-muted mb-1">Address Line 2 (optional)</label>
                  <input
                    type="text"
                    value={formData.address_line2}
                    onChange={(e) => setFormData({...formData, address_line2: e.target.value})}
                    className="w-full px-4 py-3 bg-gray-100 border border-gray-300 rounded-lg text-black focus:ring-2 focus:ring-uf-red focus:outline-none"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-uf-text-muted mb-1">City</label>
                    <input
                      type="text"
                      value={formData.city}
                      onChange={(e) => setFormData({...formData, city: e.target.value})}
                      className="w-full px-4 py-3 bg-gray-100 border border-gray-300 rounded-lg text-black focus:ring-2 focus:ring-uf-red focus:outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-uf-text-muted mb-1">Postcode</label>
                    <input
                      type="text"
                      value={formData.postcode}
                      onChange={(e) => setFormData({...formData, postcode: e.target.value})}
                      className="w-full px-4 py-3 bg-gray-100 border border-gray-300 rounded-lg text-black focus:ring-2 focus:ring-uf-red focus:outline-none"
                    />
                  </div>
                </div>
                <div className="flex gap-3 pt-4">
                  <button
                    onClick={() => setActiveSection(null)}
                    className="flex-1 py-3 border border-uf-border text-uf-text rounded-lg hover:bg-uf-surface"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSaveProfile}
                    disabled={saving}
                    className="flex-1 py-3 bg-uf-red text-white rounded-lg hover:bg-uf-red-hover disabled:opacity-50"
                  >
                    {saving ? 'Saving...' : 'Save Address'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Change Password Section */}
          {activeSection === 'password' && (
            <div className="bg-uf-surface rounded-xl border border-uf-border p-6" data-testid="change-password-section">
              <h3 className="text-lg font-semibold text-uf-text mb-4">Change Password</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm text-uf-text-muted mb-1">Current Password</label>
                  <input
                    type="password"
                    value={passwordData.current_password}
                    onChange={(e) => setPasswordData({...passwordData, current_password: e.target.value})}
                    className="w-full px-4 py-3 bg-gray-100 border border-gray-300 rounded-lg text-black focus:ring-2 focus:ring-uf-red focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm text-uf-text-muted mb-1">New Password</label>
                  <input
                    type="password"
                    value={passwordData.new_password}
                    onChange={(e) => setPasswordData({...passwordData, new_password: e.target.value})}
                    className="w-full px-4 py-3 bg-gray-100 border border-gray-300 rounded-lg text-black focus:ring-2 focus:ring-uf-red focus:outline-none"
                    placeholder="Minimum 8 characters"
                  />
                </div>
                <div>
                  <label className="block text-sm text-uf-text-muted mb-1">Confirm New Password</label>
                  <input
                    type="password"
                    value={passwordData.confirm_password}
                    onChange={(e) => setPasswordData({...passwordData, confirm_password: e.target.value})}
                    className="w-full px-4 py-3 bg-gray-100 border border-gray-300 rounded-lg text-black focus:ring-2 focus:ring-uf-red focus:outline-none"
                  />
                </div>
                {pwdError && (
                  <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3 text-red-400 text-sm flex items-center gap-2">
                    <AlertCircle className="w-4 h-4 flex-shrink-0" />
                    {pwdError}
                  </div>
                )}
                <div className="flex gap-3 pt-4">
                  <button
                    onClick={() => { setActiveSection(null); setPasswordData({ current_password: '', new_password: '', confirm_password: '' }); setPwdError(''); }}
                    className="flex-1 py-3 border border-uf-border text-uf-text rounded-lg hover:bg-uf-surface"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleChangePassword}
                    disabled={saving || !passwordData.current_password || !passwordData.new_password}
                    className="flex-1 py-3 bg-uf-red text-white rounded-lg hover:bg-uf-red-hover disabled:opacity-50"
                  >
                    {saving ? 'Changing...' : 'Change Password'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Membership Freeze Section */}
          {activeSection === 'freeze' && (
            <div className="bg-uf-surface rounded-xl border border-blue-500/30 p-6" data-testid="freeze-section">
              <h3 className="text-lg font-semibold text-uf-text mb-2 flex items-center">
                <Snowflake className="w-5 h-5 mr-2 text-blue-400" />
                Freeze Membership
              </h3>
              <p className="text-uf-text-muted text-sm mb-4">
                Pause your membership for 7-90 days. No payments will be taken during the freeze period.
              </p>
              
              <div className="bg-blue-500/10 rounded-lg p-4 mb-4">
                <p className="text-blue-400 text-sm">
                  <strong>Rules:</strong><br/>
                  • Minimum 7 days, maximum 90 days<br/>
                  • Must start at least 7 days from today<br/>
                  • One freeze per 12-month period<br/>
                  • Freeze cannot be cancelled once started
                </p>
              </div>

              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-uf-text-muted mb-1">Start Date</label>
                    <input
                      type="date"
                      value={freezeData.start_date}
                      onChange={(e) => setFreezeData({...freezeData, start_date: e.target.value})}
                      min={getMinFreezeDate()}
                      className="w-full px-4 py-3 bg-gray-100 border border-gray-300 rounded-lg text-black focus:ring-2 focus:ring-blue-500 focus:outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-uf-text-muted mb-1">End Date</label>
                    <input
                      type="date"
                      value={freezeData.end_date}
                      onChange={(e) => setFreezeData({...freezeData, end_date: e.target.value})}
                      min={freezeData.start_date || getMinFreezeDate()}
                      className="w-full px-4 py-3 bg-gray-100 border border-gray-300 rounded-lg text-black focus:ring-2 focus:ring-blue-500 focus:outline-none"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm text-uf-text-muted mb-1">Reason (optional)</label>
                  <select
                    value={freezeData.reason}
                    onChange={(e) => setFreezeData({...freezeData, reason: e.target.value})}
                    className="w-full px-4 py-3 bg-gray-100 border border-gray-300 rounded-lg text-black focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  >
                    <option value="">Select a reason</option>
                    <option value="Holiday">Holiday / Travel</option>
                    <option value="Medical">Medical / Injury</option>
                    <option value="Work">Work commitments</option>
                    <option value="Financial">Financial reasons</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                {freezeError && (
                  <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3 text-red-400 text-sm flex items-center gap-2">
                    <AlertCircle className="w-4 h-4 flex-shrink-0" />
                    {freezeError}
                  </div>
                )}
                <div className="flex gap-3 pt-4">
                  <button
                    onClick={() => { setActiveSection(null); setFreezeData({ start_date: '', end_date: '', reason: '' }); setFreezeError(''); }}
                    className="flex-1 py-3 border border-uf-border text-uf-text rounded-lg hover:bg-uf-surface"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleRequestFreeze}
                    disabled={saving || !freezeData.start_date || !freezeData.end_date}
                    className="flex-1 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50"
                  >
                    {saving ? 'Submitting...' : 'Request Freeze'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Banking Details Section */}
          {activeSection === 'banking' && (
            <div className="bg-uf-surface rounded-xl border border-uf-border p-6" data-testid="banking-section">
              <h3 className="text-lg font-semibold text-uf-text mb-4 flex items-center gap-2">
                <CreditCard className="w-5 h-5 text-emerald-400" /> Update Direct Debit Details
              </h3>

              {mandate && (
                <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-lg p-4 mb-4">
                  <p className="text-xs text-emerald-400/80 uppercase tracking-wider mb-2">Current Details</p>
                  <div className="flex gap-8 text-sm">
                    <div><span className="text-zinc-500">Account Holder:</span> <span className="text-white font-medium">{mandate.account_holder_name}</span></div>
                    <div><span className="text-zinc-500">Sort Code:</span> <span className="text-white font-mono">{mandate.sort_code?.replace(/(\d{2})(\d{2})(\d{2})/, '$1-$2-$3')}</span></div>
                    <div><span className="text-zinc-500">Account:</span> <span className="text-white font-mono">****{mandate.account_number_last4}</span></div>
                  </div>
                </div>
              )}

              {(() => {
                const today = new Date();
                const nextMonth = today.getMonth() === 11 ? new Date(today.getFullYear() + 1, 0, 1) : new Date(today.getFullYear(), today.getMonth() + 1, 1);
                const daysUntil = Math.ceil((nextMonth - today) / (1000 * 60 * 60 * 24));
                const canChange = daysUntil >= 5;
                if (!canChange) {
                  return (
                    <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-4 text-amber-400 text-sm flex items-start gap-2">
                      <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="font-medium">Changes locked</p>
                        <p className="text-xs text-amber-400/70 mt-1">Direct debit changes must be made at least 5 days before the 1st. You can make changes again from the 2nd of {nextMonth.toLocaleString('default', { month: 'long' })}.</p>
                      </div>
                    </div>
                  );
                }
                return (
                  <div className="space-y-4">
                    <p className="text-zinc-400 text-xs">Changes must be submitted at least 5 days before the 1st of the month. Your next payment will use the new details.</p>
                    <div>
                      <label className="block text-sm text-uf-text-muted mb-1">Account Holder Name</label>
                      <input type="text" value={ddForm.account_holder_name} onChange={e => setDdForm({...ddForm, account_holder_name: e.target.value})}
                        className="w-full bg-uf-surface-hi border border-uf-border rounded-lg px-3 py-2.5 text-white text-sm" placeholder="Full name as on bank account" data-testid="dd-holder-name" />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm text-uf-text-muted mb-1">Sort Code</label>
                        <input type="text" value={ddForm.sort_code} onChange={e => setDdForm({...ddForm, sort_code: e.target.value})} maxLength={8}
                          className="w-full bg-uf-surface-hi border border-uf-border rounded-lg px-3 py-2.5 text-white text-sm font-mono" placeholder="00-00-00" data-testid="dd-sort-code" />
                      </div>
                      <div>
                        <label className="block text-sm text-uf-text-muted mb-1">Account Number</label>
                        <input type="text" value={ddForm.account_number} onChange={e => setDdForm({...ddForm, account_number: e.target.value})} maxLength={8}
                          className="w-full bg-uf-surface-hi border border-uf-border rounded-lg px-3 py-2.5 text-white text-sm font-mono" placeholder="12345678" data-testid="dd-account-number" />
                      </div>
                    </div>
                    <div className="flex justify-end gap-3 pt-2">
                      <button onClick={() => { setActiveSection(null); setDdForm({ account_holder_name: '', account_number: '', sort_code: '' }); }}
                        className="px-4 py-2 bg-zinc-800 text-zinc-300 rounded-lg text-sm hover:bg-zinc-700">Cancel</button>
                      <button data-testid="dd-save-btn" disabled={ddSaving || !ddForm.account_holder_name || !ddForm.sort_code || !ddForm.account_number}
                        onClick={async () => {
                          setDdSaving(true); setMessage(null);
                          try {
                            const res = await fetch(`${API_URL}/api/auth/update-mandate`, {
                              method: 'POST', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                              body: JSON.stringify(ddForm)
                            });
                            const data = await res.json();
                            if (!res.ok) throw new Error(data.detail || 'Update failed');
                            setMessage({ type: 'success', text: 'Direct debit details updated successfully' });
                            setMandate({ ...mandate, account_holder_name: ddForm.account_holder_name, account_number_last4: data.new_last4, sort_code: data.new_sort_code });
                            setDdForm({ account_holder_name: '', account_number: '', sort_code: '' });
                            setActiveSection(null);
                          } catch (err) {
                            setMessage({ type: 'error', text: err.message });
                          }
                          setDdSaving(false);
                        }}
                        className="px-6 py-2 bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg text-sm disabled:opacity-50">
                        {ddSaving ? 'Updating...' : 'Update Details'}
                      </button>
                    </div>
                  </div>
                );
              })()}
            </div>
          )}

          {/* Freeze History */}
          {freezeHistory.length > 0 && (
            <div className="bg-uf-surface rounded-xl border border-uf-border p-6">
              <h3 className="text-lg font-semibold text-uf-text mb-4">Freeze History</h3>
              <div className="space-y-3">
                {freezeHistory.map((freeze) => (
                  <div key={freeze.id} className="flex items-center justify-between py-3 border-b border-uf-border last:border-0">
                    <div>
                      <p className="text-uf-text">{freeze.start_date} → {freeze.end_date}</p>
                      <p className="text-uf-text-muted text-sm">{freeze.reason || 'No reason given'}</p>
                    </div>
                    <span className={`text-xs px-2 py-1 rounded ${freeze.status === 'active' ? 'bg-blue-500/20 text-blue-400' : freeze.status === 'completed' ? 'bg-green-500/20 text-green-400' : 'bg-gray-500/20 text-gray-400'}`}>
                      {freeze.status}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Membership Card */}
          <div className="bg-uf-surface rounded-xl border border-uf-border p-6">
            <h3 className="text-lg font-semibold text-uf-text mb-4 flex items-center">
              <Shield className="w-5 h-5 mr-2 text-uf-red" />
              Membership Details
            </h3>

            <div className="space-y-4">
              <div className="flex items-center justify-between py-2 border-b border-uf-border">
                <span className="text-uf-text-muted">Plan</span>
                <span className="text-uf-text font-medium">{subscription?.plan_name || memberData?.plan_name || 'Standard'}</span>
              </div>

              <div className="flex items-center justify-between py-2 border-b border-uf-border">
                <span className="text-uf-text-muted">Home Gym</span>
                <span className="text-uf-text">{memberData?.home_site_name || 'Not set'}</span>
              </div>

              <div className="flex items-center justify-between py-2 border-b border-uf-border">
                <span className="text-uf-text-muted">Monthly Fee</span>
                <span className="text-uf-text font-medium">£{subscription?.monthly_amount?.toFixed(2) || '35.00'}</span>
              </div>

              {subscription?.next_payment_date && (
                <div className="flex items-center justify-between py-2">
                  <span className="text-uf-text-muted">Next Payment</span>
                  <span className="text-uf-text">{subscription.next_payment_date}</span>
                </div>
              )}
            </div>
          </div>

          {/* Payment History */}
          <div className="bg-uf-surface rounded-xl border border-uf-border p-6">
            <h3 className="text-lg font-semibold text-uf-text mb-4 flex items-center">
              <CreditCard className="w-5 h-5 mr-2 text-uf-red" />
              Recent Payments
            </h3>

            {paymentHistory.length === 0 ? (
              <p className="text-uf-text-muted text-center py-4">No payment history available</p>
            ) : (
              <div className="space-y-3">
                {paymentHistory.map((payment) => (
                  <div 
                    key={payment.id}
                    className="flex items-center justify-between py-3 border-b border-uf-border last:border-0"
                  >
                    <div>
                      <p className="text-uf-text">{payment.description || 'Monthly subscription'}</p>
                      <p className="text-uf-text-muted text-sm">{payment.collection_date}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-uf-text font-medium">£{payment.amount?.toFixed(2)}</p>
                      <span className={`text-xs ${payment.status === 'successful' ? 'text-green-400' : payment.status === 'failed' ? 'text-red-400' : 'text-yellow-400'}`}>
                        {payment.status}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Sign Out */}
          <button
            onClick={logout}
            className="w-full py-3 border border-red-500/50 text-red-400 rounded-xl hover:bg-red-500/10 flex items-center justify-center"
            data-testid="logout-btn"
          >
            <LogOut className="w-5 h-5 mr-2" />
            Sign Out
          </button>
        </div>
      </div>

      {/* Bottom Navigation */}
      <nav className="fixed bottom-0 left-0 right-0 bg-uf-surface border-t border-uf-border">
        <div className="max-w-lg mx-auto flex justify-around py-3">
          <Link to="/hub" className="flex flex-col items-center text-uf-text-muted hover:text-uf-red">
            <CreditCard className="w-6 h-6" />
            <span className="text-xs mt-1">Check-in</span>
          </Link>
          <Link to="/hub/profile" className="flex flex-col items-center text-uf-red">
            <User className="w-6 h-6" />
            <span className="text-xs mt-1">Profile</span>
          </Link>
          <Link to="/hub" className="flex flex-col items-center text-uf-text-muted hover:text-uf-red">
            <Bell className="w-6 h-6" />
            <span className="text-xs mt-1">Notifications</span>
          </Link>
        </div>
      </nav>
    </div>
  );
};

export default MemberProfile;
